import { useCallback, useMemo, type Dispatch, type SetStateAction } from 'react'
import {
  doesToggleMultiSelectFilterGroupSelectAll,
  getMultiSelectFilterAllValue,
  getMultiSelectFilterRowAction,
  selectMultiSelectFilterGroupOnly,
  selectMultiSelectFilterRow,
  toggleMultiSelectFilterGroup,
  toggleMultiSelectFilterValue,
  type MultiSelectFilterBehavior,
  type MultiSelectFilterRowAction,
} from './multiSelectFilterValue'
import { createMultiSelectFilterLookup } from './multiSelectFilterLookup'

type MultiSelectFilterState<T extends string> = {
  isAllSelected: boolean
  selectedCount: number
  isSelected: (value: T) => boolean
  toggle: (value: T) => void
  selectRow: (value: T) => void
  selectAll: VoidFunction
  getRowAction: (value: T) => MultiSelectFilterRowAction
  doesToggleSelectAll: (value: T) => boolean
  toggleGroup: (input: MultiSelectFilterGroupToggle<T>) => void
  doesToggleGroupSelectAll: (input: MultiSelectFilterGroupToggle<T>) => boolean
  selectGroupOnly: (groupValues: T[]) => void
}

export type MultiSelectFilterGroupToggle<T extends string> = {
  groupValues: T[]
  isGroupSelected: boolean
}

export const useMultiSelectFilterState = <T extends string>({
  allValues,
  selectedValues,
  setSelectedValues,
  behavior,
}: {
  allValues: T[]
  selectedValues: T[]
  setSelectedValues: Dispatch<SetStateAction<T[]>>
  behavior: MultiSelectFilterBehavior
}): MultiSelectFilterState<T> => {
  const lookup = useMemo(
    () => createMultiSelectFilterLookup({ allValues, selectedValues, behavior }),
    [allValues, behavior, selectedValues]
  )
  const { isAllSelected, selectedCount } = lookup

  const isSelected = useCallback((value: T): boolean => lookup.isSelected(value), [lookup])

  const toggle = useCallback(
    (value: T): void => {
      setSelectedValues((currentValues) =>
        toggleMultiSelectFilterValue({ allValues, selectedValues: currentValues, behavior, value })
      )
    },
    [allValues, behavior, setSelectedValues]
  )

  const selectAll = useCallback((): void => {
    setSelectedValues(getMultiSelectFilterAllValue({ allValues, behavior }))
  }, [allValues, behavior, setSelectedValues])

  const getRowAction = useCallback(
    (value: T): MultiSelectFilterRowAction =>
      getMultiSelectFilterRowAction({ isSelected: isSelected(value), isAllSelected, selectedCount }),
    [isAllSelected, isSelected, selectedCount]
  )

  const selectRow = useCallback(
    (value: T): void => {
      setSelectedValues((currentValues) =>
        selectMultiSelectFilterRow({ allValues, selectedValues: currentValues, behavior, value })
      )
    },
    [allValues, behavior, setSelectedValues]
  )

  const doesToggleGroupSelectAll = useCallback(
    ({ groupValues, isGroupSelected }: MultiSelectFilterGroupToggle<T>): boolean =>
      doesToggleMultiSelectFilterGroupSelectAll({
        allValues,
        selectedValues,
        behavior,
        groupValues,
        isGroupSelected,
      }),
    [allValues, behavior, selectedValues]
  )

  const toggleGroup = useCallback(
    ({ groupValues, isGroupSelected }: MultiSelectFilterGroupToggle<T>): void => {
      setSelectedValues((currentValues) =>
        toggleMultiSelectFilterGroup({
          allValues,
          selectedValues: currentValues,
          behavior,
          groupValues,
          isGroupSelected,
        })
      )
    },
    [allValues, behavior, setSelectedValues]
  )

  const selectGroupOnly = useCallback(
    (groupValues: T[]): void => {
      setSelectedValues((currentValues) =>
        selectMultiSelectFilterGroupOnly({ allValues, selectedValues: currentValues, behavior, groupValues })
      )
    },
    [allValues, behavior, setSelectedValues]
  )

  const doesToggleSelectAll = useCallback((value: T): boolean => lookup.doesToggleSelectAll(value), [lookup])

  return {
    isAllSelected,
    selectedCount,
    isSelected,
    toggle,
    selectRow,
    selectAll,
    getRowAction,
    doesToggleSelectAll,
    toggleGroup,
    doesToggleGroupSelectAll,
    selectGroupOnly,
  }
}
